import * as Sentry from '@sentry/nextjs';
import 'client-only';

import { sentryConfig } from '../../utils/sentry-config';
import type {
  FeedbackPayload,
  FeedbackProvider,
} from '../types/feedback-provider';

/**
 * SentryFeedbackProvider — forwards a FeedbackPayload to
 * `Sentry.captureFeedback` so the entry nests under the originating
 * issue (via `associatedEventId`) in the Sentry Feedback inbox.
 *
 * Environment: client-only (Sentry browser SDK).
 * Registered in: observability/feedback/submit-feedback.client.ts
 */
export class SentryFeedbackProvider implements FeedbackProvider {
  async submit(payload: FeedbackPayload): Promise<void> {
    // Reject when the SDK is off so submitFeedback's fan-out reports a
    // "couldn't send" state to the UI instead of falsely claiming the
    // feedback was accepted. With multiple providers later, an enabled
    // sibling can still resolve and rescue the submission.
    if (!sentryConfig.enabled) {
      throw new Error('Sentry is disabled — feedback not sent');
    }

    const feedback: {
      name: string;
      email: string;
      message: string;
      associatedEventId?: string;
    } = {
      name: payload.name,
      email: payload.email,
      message: payload.message,
    };
    if (payload.eventId) {
      feedback.associatedEventId = payload.eventId;
    }

    Sentry.captureFeedback(feedback, {
      captureContext: {
        tags: {
          contact_consent: String(payload.contactConsent),
          source: payload.source,
        },
      },
    });
  }
}
